import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CircleX, UploadIcon, Trash2 } from "lucide-react";
import { FaFile, FaFilePdf, FaFileWord } from "react-icons/fa";

export function FileUpload({
  files,
  setFiles,
  inputProps,
  containerProps,
  placeholder = "Only PDF and Word files are allowed",
}) {
  const {
    className: inputClassName = "",
    postOnChange,
    multiple = true,
    ...restInput
  } = inputProps ?? {};
  const { className: labelClassName = "", ...restLabel } = containerProps ?? {};

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null); // Ref to reset input field

  const addUniqueFiles = (newFiles) => {
    setFiles((prev) => {
      if (!multiple) {
        return newFiles.length > 0 ? [newFiles[0]] : []; // Only keep the latest file
      }
      
      const existingFileNames = new Set(prev.map((file) => file.name));
      const uniqueFiles = newFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );
  
      return [...prev, ...uniqueFiles]; // Append only new unique files
    });
  
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDragging(false);
      const droppedFiles = Array.from(event.dataTransfer.files);
      addUniqueFiles(droppedFiles);
    },
    [setFiles]
  );

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    addUniqueFiles(newFiles);

    // Call the external onChange from inputProps if provided
    if (postOnChange) {
      postOnChange(e);
    }
  };

  const handleRemoveFile = (fileName) => {
    setFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleClearAll = () => {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset input field
    }
  };

  return (
    <div>
      <div className="flex items-center justify-center w-full">
        <label
          htmlFor="dropzone-file"
          className={cn(
            "flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-[var(--gray-300)] rounded-lg cursor-pointer bg-[var(--gray-100)] transition-colors duration-100",
            isDragging && "bg-[var(--gray-200)] border-sky-500",
            labelClassName
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          {...restLabel}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <UploadIcon className="w-8 h-8 text-[var(--gray-400)]" />
            <p className="mb-2 text-xs md:text-sm text-gray-500 dark:text-gray-400">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {placeholder}
            </p>
          </div>
          <input
            ref={fileInputRef}
            id="dropzone-file"
            type="file"
            className={cn("hidden", inputClassName)}
            onChange={handleFileChange}
            multiple={multiple}
            {...restInput}
          />
        </label>
      </div>

      {files.length > 0 && (
        <div className="flex flex-col space-y-2 mt-2">
          <Button
            variant="secondary"
            className="self-end text-xs text-muted-foreground hover:text-destructive rounded-lg [&_svg]:h-3 h-8 p-2"
            onClick={handleClearAll}
          >
            <CircleX /> Clear All
          </Button>
          {files.map((file) => (
            <div
              className="flex items-center justify-between border rounded-md p-2"
              key={file.name}
            >
              <div className="flex items-center space-x-2">
                {extensionToIconMap[file.type] || extensionToIconMap.default}
                <p className="text-xs">{file.name}</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => handleRemoveFile(file.name)}
                className="group h-8 w-8 [&_svg]:text-muted-foreground/50 hover:group [&_svg]:hover:text-destructive"
              >
                <CircleX />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const extensionToIconMap = {
  "application/pdf": (
    <FaFilePdf className="h-4 w-4 self-center text-[var(--red-500)] rounded-md" />
  ),
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": (
    <FaFileWord className="h-4 w-4 self-center text-[var(--blue-500)] rounded-md" />
  ),
  "application/msword": (
    <FaFileWord className="h-4 w-4 self-center text-[var(--blue-500)] rounded-md" />
  ),
  default: (
    <FaFile className="h-6 w-6 self-center text-[var(--gray-500)] rounded-md" />
  ),
};
